/**
 * demoJornadaLifecycle.ts — Jornada demo multicaja siempre visible en el día local.
 *
 * QUÉ HACE: Re-ancla cierres/ventas/movs demo al calendario de hoy en cada arranque.
 * Si el usuario registró ventas o cierres reales, no re-inyecta demo (incluso en modo demo).
 *
 * QUÉ EXPORTA: ensureDemoJornadaForToday, hasUserRealCajaActivity, purgeDemoJornadaFromStorage
 * QUÉ NO DEBE HACER: Importar React ni mutar datos canónicos del seed.
 */

import { logger } from "@/lib/logger";
import { readCreditosRawConLegacy } from "@/features/caja/utils/readCreditoFromStorage";
import {
  runTransaction,
  txRead,
  txReadRaw,
  txRemove,
  txWrite,
  txWriteRaw,
} from "@/lib/persistence/transactionalStore";
import type { CierreCaja, MovimientoCaja, Venta } from "../types/caja.types";
import { isTicketDemoVenta } from "../fixtures/ticketRegressionCart";
import { MAY12_DEMO_SEED_VERSION } from "./may12CajaDemoSeed.data";
import {
  buildMulticajaJornadaCierres,
  getMulticajaDemoCierreIds,
} from "./multicajaJornadaDemo.data";
import {
  ANEXO_B_CIERRE_DEMO_ID,
  buildAnexoBCierreDemo,
  ANEXO_B_SESION_DEMO_ID,
} from "./anexoBCierreDemo.data";
import {
  localCalendarDayIso,
  shiftCajaDemoAnchorDatesDeep,
} from "./shiftCajaDemoMay12AnchorToLocalDay";
import {
  ensureDemoHistorialSynced,
  isDemoCreditoId,
  isDemoMovimientoId,
  isDemoVentaId,
} from "./syncDemoHistorialFromCierres";
import { ensureDemoStockSyncedFromHistorial } from "./syncDemoStockMovementsFromVentas";
import type { StockMovimiento } from "@/types";

export const LS_DEMO_CALENDAR_DAY = "altenot_caja_demo_calendar_day";
/** Marca persistente: el usuario ya vendió en POS real (aunque la sesión sea mock-token). */
export const LS_USER_REAL_POS = "altenot_caja_user_real_pos";
const LS_CIERRE_SEED_VERSION = "altenot_caja_demo_cierre_seed";
const LS_STOCK_SYNC_VERSION = "altenot_caja_demo_stock_sync";
const LS_HISTORIAL_SYNC_VERSION = "altenot_caja_demo_historial_sync";

const MULTICAJA_DEMO_CIERRE_ID_SET = new Set(getMulticajaDemoCierreIds());
const DEMO_CIERRE_ID_SET = new Set([
  ...MULTICAJA_DEMO_CIERRE_ID_SET,
  ANEXO_B_CIERRE_DEMO_ID,
]);

function readJsonArray<T>(key: string): T[] {
  const parsed = txRead<T[] | null>(key, null);
  return Array.isArray(parsed) ? parsed : [];
}

/** Persiste que el usuario dejó de usar solo datos demo de caja. */
export function markUserRealCajaActivity(): void {
  txWriteRaw(LS_USER_REAL_POS, "1");
}

export function isDemoCierreId(id: string | undefined | null): boolean {
  return !!id && DEMO_CIERRE_ID_SET.has(id);
}

function isAnexoBDemoSession(sesionId: string | undefined): boolean {
  return sesionId === ANEXO_B_SESION_DEMO_ID;
}

const AUDIT_LOGS_KEY = "auditoria_logs_v1";
/** Acciones de Auditoría que prueban actividad real en el POS de Caja. */
const CAJA_REAL_AUDIT_ACTIONS = new Set([
  "caja_venta_completada",
  "venta_creada",
  "caja_retiro_registrado",
  "caja_gasto_registrado",
  "caja_ingreso_registrado",
  "caja_abrir_caja_registrado",
]);

/**
 * True si Auditoría conserva alguna venta/movimiento real de caja. Sirve como
 * red de seguridad cuando el historial local se vació/parcializó: si Auditoría
 * lo tiene, el usuario NO debe volver a modo demo (eso re-sembraría y podría
 * sobrescribir datos reales).
 */
function hasRealCajaActivityInAudit(): boolean {
  const logs = txRead<Array<{
    action?: string;
    entidadId?: string;
    details?: Record<string, unknown>;
  }> | null>(AUDIT_LOGS_KEY, null);
  if (!Array.isArray(logs) || logs.length === 0) return false;

  return logs.some((log) => {
    if (!log || typeof log.action !== "string") return false;
    if (!CAJA_REAL_AUDIT_ACTIONS.has(log.action)) return false;
    const entidadId = String(log.entidadId ?? "");
    if (
      entidadId &&
      (isDemoVentaId(entidadId) || isDemoMovimientoId(entidadId))
    ) {
      return false;
    }
    const sesionId =
      typeof log.details?.sesionId === "string" ? log.details.sesionId : "";
    if (sesionId === ANEXO_B_SESION_DEMO_ID) return false;
    return true;
  });
}

/** Ventas/cierres que no son jornada demo ni ticket de regresión. */
export function hasUserRealCajaActivity(): boolean {
  // Solo verifica el flag EXCLUSIVO de actividad POS real.
  // No usa hasRealActivityStarted() para evitar que actividad de Auditoría,
  // Inventario u otros módulos oculte incorrectamente los datos demo de Caja.
  const ventas = readJsonArray<Venta>("caja_ventas_historial");
  const cierres = readJsonArray<CierreCaja>("caja_cierres_historial");
  const hasRealVenta = ventas.some(
    (v) =>
      !isDemoVentaId(v.id) &&
      !isTicketDemoVenta(v.id) &&
      !isAnexoBDemoSession(v.sesionId),
  );
  const hasRealCierre = cierres.some(
    (c) => c?.id && !DEMO_CIERRE_ID_SET.has(c.id),
  );
  if (hasRealVenta || hasRealCierre) {
    markUserRealCajaActivity();
    return true;
  }

  // Marca persistente de POS real. NO se borra por tener el historial local
  // vacío o parcial: hacerlo permitía re-sembrar demo y sobrescribir datos
  // reales tras un kill del APK, una restauración parcial o un fallo de
  // escritura (la causa del incidente reportado).
  if (txReadRaw(LS_USER_REAL_POS) === "1") return true;

  // Rastro en Auditoría: si el historial local se perdió pero Auditoría
  // conserva ventas/movimientos reales, seguimos en modo real (nunca demo).
  if (hasRealCajaActivityInAudit()) {
    markUserRealCajaActivity();
    return true;
  }

  return false;
}

/** Oculta ventas demo cuando el usuario ya operó caja de verdad. */
export function filterVentasForUserHistorial(ventas: Venta[]): Venta[] {
  if (!hasUserRealCajaActivity()) return ventas;
  return ventas.filter(
    (v) =>
      !isDemoVentaId(v.id) &&
      !isTicketDemoVenta(v.id) &&
      !isAnexoBDemoSession(v.sesionId),
  );
}

/** Oculta movimientos demo cuando el usuario ya operó caja de verdad. */
export function filterMovimientosForUserHistorial(
  movimientos: MovimientoCaja[],
): MovimientoCaja[] {
  if (!hasUserRealCajaActivity()) return movimientos;
  return movimientos.filter(
    (m) => !isDemoMovimientoId(m.id) && !isAnexoBDemoSession(m.sesionId),
  );
}

/** Oculta cierres demo cuando el usuario ya operó caja de verdad. */
export function filterCierresForUserHistorial(
  cierres: CierreCaja[],
): CierreCaja[] {
  if (!hasUserRealCajaActivity()) return cierres;
  return cierres.filter((c) => !isDemoCierreId(c.id));
}

function buildFreshDemoJornadaForToday(): CierreCaja[] {
  return [
    ...shiftCajaDemoAnchorDatesDeep(
      buildMulticajaJornadaCierres(),
      localCalendarDayIso(),
    ),
    buildAnexoBCierreDemo(),
  ];
}

function mergeDemoCierresIntoHistorial(fresh: CierreCaja[]): CierreCaja[] {
  const existing = readJsonArray<CierreCaja>("caja_cierres_historial");
  const userCierres = existing.filter((c) => !DEMO_CIERRE_ID_SET.has(c.id));
  return [...fresh, ...userCierres];
}

function demoCierresAnchoredToDay(cierres: CierreCaja[], day: string): boolean {
  const demo = cierres.filter((c) => MULTICAJA_DEMO_CIERRE_ID_SET.has(c.id));
  if (demo.length === 0) return false;
  return demo.every((c) => {
    const fecha = c.fechaApertura || c.abiertaEn || "";
    return fecha.startsWith(day);
  });
}

/** Elimina cierres/ventas/movs demo del LS (conserva datos reales del usuario). */
export function purgeDemoJornadaFromStorage(): boolean {
  const ventas = readJsonArray<Venta>("caja_ventas_historial");
  const movs = readJsonArray<MovimientoCaja>("caja_movimientos");
  const creditos = readCreditosRawConLegacy() ?? [];
  const cierres = readJsonArray<CierreCaja>("caja_cierres_historial");
  const stockMovs = readJsonArray<StockMovimiento>("inventory_stock_movements");

  const nextVentas = ventas.filter(
    (v) =>
      !isDemoVentaId(v.id) &&
      !isTicketDemoVenta(v.id) &&
      !isAnexoBDemoSession(v.sesionId),
  );
  const nextMovs = movs.filter(
    (m) => !isDemoMovimientoId(m.id) && !isAnexoBDemoSession(m.sesionId),
  );
  const nextCreditos = creditos.filter(
    (f) => !isDemoCreditoId(f.id) && !isAnexoBDemoSession(f.sesionId),
  );
  const nextCierres = cierres.filter((c) => !DEMO_CIERRE_ID_SET.has(c.id));
  const nextStockMovs = stockMovs.filter(
    (m) => !String(m.id).startsWith("demo-sm-"),
  );

  const changed =
    nextVentas.length !== ventas.length ||
    nextMovs.length !== movs.length ||
    nextCreditos.length !== creditos.length ||
    nextCierres.length !== cierres.length ||
    nextStockMovs.length !== stockMovs.length;

  if (!changed) return false;

  // Escritura atómica: purgar las 5 claves demo en una sola transacción evita
  // estados intermedios (ej. ventas purgadas pero cierres no) ante un crash.
  runTransaction((): void => {
    txWrite("caja_ventas_historial", nextVentas);
    txWrite("caja_movimientos", nextMovs);
    txWrite("caja_creditos", nextCreditos);
    txWrite("caja_cierres_historial", nextCierres);
    txWrite("inventory_stock_movements", nextStockMovs);
    txRemove(LS_HISTORIAL_SYNC_VERSION);
    txRemove(LS_STOCK_SYNC_VERSION);
  });

  try {
    window.dispatchEvent(new CustomEvent("caja-demo-historial-synced"));
  } catch {
    /* ignore */
  }

  return true;
}

/** Llamar tras registrar una venta real en POS (limpia jornada demo). */
export function onUserRealCajaSaleRecorded(): void {
  const alreadyReal = txReadRaw(LS_USER_REAL_POS) === "1";
  const demoCalendarDay = txReadRaw(LS_DEMO_CALENDAR_DAY);
  markUserRealCajaActivity();
  // La purga recorre 5 historiales completos. Una vez el usuario ya es "real"
  // y no queda jornada demo anclada, se evita en cada venta (costo innecesario).
  if (!alreadyReal || demoCalendarDay) {
    purgeDemoJornadaFromStorage();
  }
  txRemove(LS_DEMO_CALENDAR_DAY);
  try {
    window.dispatchEvent(new CustomEvent("caja-real-activity-started"));
  } catch {
    /* ignore */
  }
}

/**
 * Garantiza la jornada demo (40 cierres / 240 ventas, 4 turnos incl. madrugada)
 * en el día calendario local.
 * @returns true si hubo escritura o re-sync
 */
export function ensureDemoJornadaForToday(options?: {
  force?: boolean;
}): boolean {
  if (hasUserRealCajaActivity()) {
    purgeDemoJornadaFromStorage();
    return false;
  }

  const today = localCalendarDayIso();
  const storedDay: string | null = txReadRaw(LS_DEMO_CALENDAR_DAY);

  const cierres = readJsonArray<CierreCaja>("caja_cierres_historial");
  const hasDemoCierres = cierres.some((c) => DEMO_CIERRE_ID_SET.has(c.id));
  const needsSeed = cierres.length === 0 || !hasDemoCierres;
  const needsDayShift =
    storedDay !== today || !demoCierresAnchoredToDay(cierres, today);

  if (!options?.force && !needsSeed && !needsDayShift) {
    ensureDemoHistorialSynced();
    return false;
  }

  const fresh = buildFreshDemoJornadaForToday();
  runTransaction((): void => {
    txWrite("caja_cierres_historial", mergeDemoCierresIntoHistorial(fresh));
    txWriteRaw(LS_DEMO_CALENDAR_DAY, today);
    txWriteRaw(LS_CIERRE_SEED_VERSION, MAY12_DEMO_SEED_VERSION);
  });

  ensureDemoHistorialSynced({ force: true });
  ensureDemoStockSyncedFromHistorial();
  return true;
}

/** Igual que ensureDemoJornadaForToday pero no rompe handlers MSW si falla. */
export function safeEnsureDemoJornadaForToday(options?: {
  force?: boolean;
}): boolean {
  try {
    return ensureDemoJornadaForToday(options);
  } catch (err) {
    logger.warn("[caja-demo] ensureDemoJornadaForToday:", err);
    return false;
  }
}
